import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { getCustomLinkListAsync, patchCustomLinkAsync, postCustomLinkAsync } from "src/api/customLink/custom-link";
import { CustomLinkAddParams } from "src/api/customLink/custom-link-types";
import { useApiOperation } from "src/api/hooks";
import { postPartnerCustomLinkIconUploadAsync } from "src/api/partner";
import { MediaFile } from "src/api/public-types";
import { BaseButton, BaseInput } from "src/components";
import BaseImageUpload, { BaseImageFile } from "src/components/BaseImageUpload";
import EditCancelButton from "src/components/EditCancelButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import icGlobe from "src/assets/images/ic_globe.svg";

const BasicInfoForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // location search (url query parameter) 를 읽어서 object 로 변환
  const queryParams = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );
  const { executeAsync: getCustomLinkDetail } = useApiOperation(getCustomLinkListAsync);
  const { executeAsync: patchCustomLink } = useApiOperation(patchCustomLinkAsync);
  const { executeAsync: postCustomLink } = useApiOperation(postCustomLinkAsync);
  const { executeAsync: postPartnerCustomLinkIconUpload } = useApiOperation(postPartnerCustomLinkIconUploadAsync);

  const [iconImages, setIconImages] = useState<BaseImageFile[]>([]);

  const { handleSubmit, control, watch, setValue } = useForm<CustomLinkAddParams>({
    defaultValues: {
      name: "",
      description: "",
      url: "",
    },
  });

  const id: number | undefined = queryParams?.id ? Number(queryParams.id) : undefined;

  useEffect(() => {
    const fetchApi = async (id: string) => {
      const { data } = await getCustomLinkDetail({ id });
      setValue("name", data.data.content[0].name);
      setValue("description", data.data.content[0].description);
      setValue("url", data.data.content[0].url || "");
      if (data.data.content[0].icon?.media) {
        setIconImages([{ ...data.data.content[0].icon.media, url: data.data.content[0].icon.url }]);
      }
    };
    if (id) {
      fetchApi(String(id));
    }
  }, [id]);
  const onSubmit = async (data: CustomLinkAddParams) => {
    const submitData: CustomLinkAddParams = {
      ...data,
      icon: iconImages.length > 0 ? iconImages[0] as MediaFile : {},
    };

    if (id) {
      const response = await patchCustomLink({ id: String(id), ...submitData });
      navigate(PagePath.customLink.detail.replace(":id", `${response.data.data.customLink.id}`));
    } else {
      const response = await postCustomLink(submitData);
      navigate(PagePath.customLink.detail.replace(":id", `${response.data.data.customLink.id}`));
    }
  };
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap">
            {/* 기본정보 */}
            <article className="contents-container__wrap-article">
              <div className="flex-center-between">
                <BaseSectionTitle title={"링크 정보"} />
                <div className="flex-center"></div>
              </div>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={""}>적용 미리보기</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <div className="custom-link-add-example">
                      <img
                        className="custom-link-add-example__icon"
                        src={iconImages.length > 0 ? (iconImages[0].blobUrl || iconImages[0].url || icGlobe) : icGlobe}
                        alt="link icon"
                      />
                    </div>
                    <ul className="base-list">
                      <li>아이콘은 아래 가이드에 부합하는 파일로 업로드해 주세요.</li>
                      <li>가이드에 맞지 않는 이미지 업로드 시, 앱에서 아이콘이 깨지거나 비정상적으로 노출될 수 있습니다.</li>
                      <li>별도의 아이콘을 등록하지 않을 경우, 시스템 기본 아이콘이 자동으로 노출됩니다.</li>
                    </ul>
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={""}>아이콘</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <BaseImageUpload
                      type="LOGO"
                      images={iconImages}
                      setImages={setIconImages}
                      customUploadApi={postPartnerCustomLinkIconUpload}
                      fileMaxLength={1}
                      accept=".png"
                      maxSize={100}
                      maxSizeUnit="KB"
                      customDescription={<ul className="base-list mt10"><li>파일 확장자는 .png로 업로드해야 합니다.
                      </li>
                        <li>권장 크기는 90x90 입니다.</li>
                        <li>업로드 용량은 최대 100KB까지 가능합니다.</li></ul>
                      }
                    />
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={"required"}>링크명</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <Controller
                      control={control}
                      name="name"
                      rules={{ required: { value: true, message: "필수 입력 항목입니다." } }}
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <BaseInput
                          maxLength={20}
                          onChange={onChange} //
                          value={value}
                          placeholder="Taap에서 노출할 링크명을 입력해 주세요. (5자 이내 권장)"
                          errorText={errors.name?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={""}>링크 설명</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <Controller
                      control={control}
                      name="description"
                      rules={{ maxLength: { value: 20, message: "최대 20자이내로 링크명을 입력해주세요." } }}
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <BaseInput
                          maxLength={20}
                          onChange={onChange} //
                          value={value}
                          placeholder="링크 설명을 입력해 주세요."
                          errorText={errors.description?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={"required"}>링크</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <Controller
                      control={control}
                      name="url"
                      rules={{
                        required: { value: true, message: "필수 입력 항목입니다." },
                        validate: (value) => {
                          if (value && !value.startsWith("http://") && !value.startsWith("https://")) {
                            return "http:// 또는 https:// 로 시작하는 값을 입력해주세요.";
                          }
                          return true;
                        },
                      }}
                      render={({ field: { onChange, value }, formState: { errors } }) => (
                        <BaseInput
                          onChange={onChange} //
                          value={value}
                          placeholder="Taap에 노출할 URL을 입력해 주세요."
                          errorText={errors.url?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </section>
            </article>
          </div>
        </div>

        {/* 버튼영역 */}
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>

          <div className="right-area">
            <div className="flex-center">
              <EditCancelButton to={PagePath.customLink.list} />
              <BaseButton title="저장" className="size-large" type="submit" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoForm;
